import { useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Upload, X, Images } from 'lucide-react';
import { MediaItemCard } from './MediaItemCard';
import { MediaUploadProgress } from './MediaUploadProgress';
import type { MediaItem } from '../hooks/useMediaGallery';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { env } from '@/core/config/env';

const API_URL = env.API_BASE_URL;

// Helper function to upload a single file via backend proxy (no CORS issues)
async function uploadFileViaBackend(
  file: File,
  accessToken: string,
  onProgress?: (progress: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 201) {
        try {
          const response = JSON.parse(xhr.responseText);
          // Use publicUrl (full S3 URL) from backend - avoids needing S3 env vars on frontend
          resolve(response.publicUrl || response.fileKey);
        } catch (err) {
          reject(new Error('Invalid response from server'));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.message || `Upload failed with status ${xhr.status}`));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('POST', `${API_URL}/files/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.send(formData);
  });
}

type MediaGalleryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  items: MediaItem[];
  onItemsChange: (items: MediaItem[]) => void;
  onAddItem: (item: Omit<MediaItem, 'displayOrder' | 'isPrimary' | 'id'>) => void;
  onRemoveItem: (itemId: string) => void;
  onReorder: (startIndex: number, endIndex: number) => void;
  onSetPrimary: (itemId: string) => void;
  onCaptionChange: (itemId: string, caption: string) => void;
};

type UploadingFile = {
  fileName: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
};

/**
 * MediaGalleryModal - Full media gallery management interface
 * 
 * Features:
 * - Upload multiple images/videos
 * - Drag-drop reordering
 * - Caption editing
 * - Primary image selection
 * - Remove items with confirmation
 * - Upload progress tracking
 * - 5MB limit for images, 100MB for videos
 * - Format validation
 * 
 * Integration:
 * - Uses useMediaGallery hook for state
 * - Uses MediaItemCard for individual items
 * - Uses MediaUploadProgress for upload feedback
 * - Uses @dnd-kit for drag-drop
 * 
 * Technical:
 * - Automatic is_primary enforcement (only one primary)
 * - Sequential display_order (0, 1, 2, ...)
 * - S3 upload via FileUploadService
 */
export function MediaGalleryModal({
  isOpen,
  onClose,
  items,
  onItemsChange: _onItemsChange,
  onAddItem,
  onRemoveItem,
  onReorder,
  onSetPrimary,
  onCaptionChange,
}: MediaGalleryModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const { getAccessToken } = useAuth();

  // Drag-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploadError(undefined);

    // Process files sequentially to avoid overwhelming the upload service
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        setUploadError('Only image and video files are allowed');
        continue;
      }

      // Validate file size
      const maxSize = isImage ? 5 * 1024 * 1024 : 100 * 1024 * 1024; // 5MB for images, 100MB for videos
      if (file.size > maxSize) {
        setUploadError(
          `${file.name} is too large. Max size: ${isImage ? '5MB' : '100MB'}`
        );
        continue;
      }

      // Add to uploading list
      const uploadingFile: UploadingFile = {
        fileName: file.name,
        progress: 0,
        status: 'uploading',
      };

      setUploadingFiles((prev) => [...prev, uploadingFile]);

      try {
        // Get access token for API calls
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Authentication required');
        }
        
        // Upload file via backend proxy (no CORS issues)
        const fileKey = await uploadFileViaBackend(file, token, (progress) => {
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.fileName === file.name
                ? { ...f, progress: Math.min(progress, 100) }
                : f
            )
          );
        });

        // Add to gallery with the S3 file key
        onAddItem({
          mediaType: isImage ? 'image' : 'video',
          mediaUrl: fileKey,
          caption: '',
          isNew: true,
        });

        // Mark as success
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.fileName === file.name ? { ...f, status: 'success' } : f
          )
        );

        // Remove from uploading list after 2 seconds
        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((f) => f.fileName !== file.name));
        }, 2000);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.fileName === file.name
              ? { ...f, status: 'error', error: error instanceof Error ? error.message : 'Upload failed' }
              : f
          )
        );
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Images className="h-5 w-5" />
            Manage Photo Gallery
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Upload Section */}
          <div className="mb-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleUploadClick}
              disabled={uploadingFiles.some((f) => f.status === 'uploading')}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Photos/Videos
            </Button>
            <p className="mt-2 text-xs text-gray-500">
              Supported: Images (5MB max), Videos (100MB max)
            </p>
            {uploadError && (
              <p className="mt-2 text-sm text-red-600">{uploadError}</p>
            )}
          </div>

          {/* Upload Progress */}
          {uploadingFiles.length > 0 && (
            <div className="mb-6 space-y-2">
              {uploadingFiles.map((file) => (
                <MediaUploadProgress
                  key={file.fileName}
                  fileName={file.fileName}
                  progress={file.progress}
                  status={file.status}
                  error={file.error}
                />
              ))}
            </div>
          )}

          {/* Gallery Items */}
          {items.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {items.map((item) => (
                    <MediaItemCard
                      key={item.id}
                      item={item}
                      onRemove={onRemoveItem}
                      onSetPrimary={onSetPrimary}
                      onCaptionChange={onCaptionChange}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-300 rounded-lg">
              <Images className="h-12 w-12 text-gray-400 mb-3" />
              <p className="text-sm font-medium text-gray-900">No media items yet</p>
              <p className="text-xs text-gray-500 mt-1">
                Upload photos and videos to build your gallery
              </p>
            </div>
          )}

          {/* Helper Text */}
          {items.length > 0 && (
            <div className="mt-6 rounded-lg bg-blue-50 p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">
                Tips for managing your gallery:
              </p>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>Drag items using the grip handle to reorder</li>
                <li>Set one image as "Primary" to be featured on your profile</li>
                <li>Add captions to provide context for your photos</li>
                <li>Click "Remove" twice to delete an item</li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            <X className="mr-2 h-4 w-4" />
            Close
          </Button>
        </DialogFooter>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </DialogContent>
    </Dialog>
  );
}

